import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  Optional,
  Self,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NgControl,
  ReactiveFormsModule,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TextService } from '../../core/services/text-service';
import { FormDataService } from '../../core/services/form-data-service';
import {
  clampNationalDigits,
  extractNationalDigits,
  formatPhoneWithDialCode,
  getPhoneCountryRule,
  phoneStartsWithDialCode,
} from '../../core/utils/phone-numbers';

interface PhoneCountry {
  code: string;
  dial: string;
  flag: string;
  name: string;
}

@Component({
  selector: 'app-phone-number-field',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './phone-number-field.html',
  styleUrls: ['./phone-number-field.scss'],
})
export class PhoneNumberField implements OnInit, ControlValueAccessor {
  phoneControl = new FormControl('');

  private onChange: (v: string | null) => void = () => {};
  onTouched: () => void = () => {};

  countries: PhoneCountry[] = [
    { code: 'AF', dial: '+93', flag: '🇦🇫', name: 'Afghanistan' },
    { code: 'AL', dial: '+355', flag: '🇦🇱', name: 'Albania' },
    { code: 'DZ', dial: '+213', flag: '🇩🇿', name: 'Algeria' },
    { code: 'AS', dial: '+1684', flag: '🇦🇸', name: 'American Samoa' },
    { code: 'AD', dial: '+376', flag: '🇦🇩', name: 'Andorra' },
    { code: 'AO', dial: '+244', flag: '🇦🇴', name: 'Angola' },
    { code: 'AI', dial: '+1264', flag: '🇦🇮', name: 'Anguilla' },
    { code: 'AG', dial: '+1268', flag: '🇦🇬', name: 'Antigua and Barbuda' },
    { code: 'AR', dial: '+54', flag: '🇦🇷', name: 'Argentina' },
    { code: 'AM', dial: '+374', flag: '🇦🇲', name: 'Armenia' },
    { code: 'AU', dial: '+61', flag: '🇦🇺', name: 'Australia' },
    { code: 'AT', dial: '+43', flag: '🇦🇹', name: 'Austria' },
    { code: 'AZ', dial: '+994', flag: '🇦🇿', name: 'Azerbaijan' },
    { code: 'BS', dial: '+1242', flag: '🇧🇸', name: 'Bahamas' },
    { code: 'BH', dial: '+973', flag: '🇧🇭', name: 'Bahrain' },
    { code: 'BD', dial: '+880', flag: '🇧🇩', name: 'Bangladesh' },
    { code: 'BB', dial: '+1246', flag: '🇧🇧', name: 'Barbados' },
    { code: 'BY', dial: '+375', flag: '🇧🇾', name: 'Belarus' },
    { code: 'BE', dial: '+32', flag: '🇧🇪', name: 'Belgium' },
    { code: 'BZ', dial: '+501', flag: '🇧🇿', name: 'Belize' },
    { code: 'BJ', dial: '+229', flag: '🇧🇯', name: 'Benin' },
    { code: 'BM', dial: '+1441', flag: '🇧🇲', name: 'Bermuda' },
    { code: 'BT', dial: '+975', flag: '🇧🇹', name: 'Bhutan' },
    { code: 'BO', dial: '+591', flag: '🇧🇴', name: 'Bolivia' },
    { code: 'BA', dial: '+387', flag: '🇧🇦', name: 'Bosnia and Herzegovina' },
    { code: 'BW', dial: '+267', flag: '🇧🇼', name: 'Botswana' },
    { code: 'BR', dial: '+55', flag: '🇧🇷', name: 'Brazil' },
    { code: 'BN', dial: '+673', flag: '🇧🇳', name: 'Brunei Darussalam' },
    { code: 'BG', dial: '+359', flag: '🇧🇬', name: 'Bulgaria' },
    { code: 'BF', dial: '+226', flag: '🇧🇫', name: 'Burkina Faso' },
    { code: 'BI', dial: '+257', flag: '🇧🇮', name: 'Burundi' },
    { code: 'KH', dial: '+855', flag: '🇰🇭', name: 'Cambodia' },
    { code: 'CM', dial: '+237', flag: '🇨🇲', name: 'Cameroon' },
    { code: 'CA', dial: '+1', flag: '🇨🇦', name: 'Canada' },
    { code: 'CV', dial: '+238', flag: '🇨🇻', name: 'Cape Verde' },
    { code: 'CL', dial: '+56', flag: '🇨🇱', name: 'Chile' },
    { code: 'CN', dial: '+86', flag: '🇨🇳', name: 'China' },
    { code: 'CO', dial: '+57', flag: '🇨🇴', name: 'Colombia' },
    { code: 'CR', dial: '+506', flag: '🇨🇷', name: 'Costa Rica' },
    { code: 'HR', dial: '+385', flag: '🇭🇷', name: 'Croatia' },
    { code: 'CU', dial: '+53', flag: '🇨🇺', name: 'Cuba' },
    { code: 'CY', dial: '+357', flag: '🇨🇾', name: 'Cyprus' },
    { code: 'CZ', dial: '+420', flag: '🇨🇿', name: 'Czech Republic' },
    { code: 'DK', dial: '+45', flag: '🇩🇰', name: 'Denmark' },
    { code: 'DO', dial: '+1', flag: '🇩🇴', name: 'Dominican Republic' },
    { code: 'EC', dial: '+593', flag: '🇪🇨', name: 'Ecuador' },
    { code: 'EG', dial: '+20', flag: '🇪🇬', name: 'Egypt' },
    { code: 'SV', dial: '+503', flag: '🇸🇻', name: 'El Salvador' },
    { code: 'EE', dial: '+372', flag: '🇪🇪', name: 'Estonia' },
    { code: 'ET', dial: '+251', flag: '🇪🇹', name: 'Ethiopia' },
    { code: 'FI', dial: '+358', flag: '🇫🇮', name: 'Finland' },
    { code: 'FR', dial: '+33', flag: '🇫🇷', name: 'France' },
    { code: 'DE', dial: '+49', flag: '🇩🇪', name: 'Germany' },
    { code: 'GH', dial: '+233', flag: '🇬🇭', name: 'Ghana' },
    { code: 'GR', dial: '+30', flag: '🇬🇷', name: 'Greece' },
    { code: 'GT', dial: '+502', flag: '🇬🇹', name: 'Guatemala' },
    { code: 'HT', dial: '+509', flag: '🇭🇹', name: 'Haiti' },
    { code: 'HN', dial: '+504', flag: '🇭🇳', name: 'Honduras' },
    { code: 'HK', dial: '+852', flag: '🇭🇰', name: 'Hong Kong' },
    { code: 'HU', dial: '+36', flag: '🇭🇺', name: 'Hungary' },
    { code: 'IN', dial: '+91', flag: '🇮🇳', name: 'India' },
    { code: 'ID', dial: '+62', flag: '🇮🇩', name: 'Indonesia' },
    { code: 'IR', dial: '+98', flag: '🇮🇷', name: 'Iran' },
    { code: 'IQ', dial: '+964', flag: '🇮🇶', name: 'Iraq' },
    { code: 'IE', dial: '+353', flag: '🇮🇪', name: 'Ireland' },
    { code: 'IL', dial: '+972', flag: '🇮🇱', name: 'Israel' },
    { code: 'IT', dial: '+39', flag: '🇮🇹', name: 'Italy' },
    { code: 'JM', dial: '+1876', flag: '🇯🇲', name: 'Jamaica' },
    { code: 'JP', dial: '+81', flag: '🇯🇵', name: 'Japan' },
    { code: 'JO', dial: '+962', flag: '🇯🇴', name: 'Jordan' },
    { code: 'KE', dial: '+254', flag: '🇰🇪', name: 'Kenya' },
    { code: 'KR', dial: '+82', flag: '🇰🇷', name: 'South Korea' },
    { code: 'KW', dial: '+965', flag: '🇰🇼', name: 'Kuwait' },
    { code: 'LB', dial: '+961', flag: '🇱🇧', name: 'Lebanon' },
    { code: 'LU', dial: '+352', flag: '🇱🇺', name: 'Luxembourg' },
    { code: 'MY', dial: '+60', flag: '🇲🇾', name: 'Malaysia' },
    { code: 'MX', dial: '+52', flag: '🇲🇽', name: 'Mexico' },
    { code: 'MA', dial: '+212', flag: '🇲🇦', name: 'Morocco' },
    { code: 'NL', dial: '+31', flag: '🇳🇱', name: 'Netherlands' },
    { code: 'NZ', dial: '+64', flag: '🇳🇿', name: 'New Zealand' },
    { code: 'NG', dial: '+234', flag: '🇳🇬', name: 'Nigeria' },
    { code: 'NO', dial: '+47', flag: '🇳🇴', name: 'Norway' },
    { code: 'PK', dial: '+92', flag: '🇵🇰', name: 'Pakistan' },
    { code: 'PA', dial: '+507', flag: '🇵🇦', name: 'Panama' },
    { code: 'PE', dial: '+51', flag: '🇵🇪', name: 'Peru' },
    { code: 'PH', dial: '+63', flag: '🇵🇭', name: 'Philippines' },
    { code: 'PL', dial: '+48', flag: '🇵🇱', name: 'Poland' },
    { code: 'PT', dial: '+351', flag: '🇵🇹', name: 'Portugal' },
    { code: 'PR', dial: '+1', flag: '🇵🇷', name: 'Puerto Rico' },
    { code: 'QA', dial: '+974', flag: '🇶🇦', name: 'Qatar' },
    { code: 'RO', dial: '+40', flag: '🇷🇴', name: 'Romania' },
    { code: 'RU', dial: '+7', flag: '🇷🇺', name: 'Russia' },
    { code: 'SA', dial: '+966', flag: '🇸🇦', name: 'Saudi Arabia' },
    { code: 'RS', dial: '+381', flag: '🇷🇸', name: 'Serbia' },
    { code: 'SG', dial: '+65', flag: '🇸🇬', name: 'Singapore' },
    { code: 'ZA', dial: '+27', flag: '🇿🇦', name: 'South Africa' },
    { code: 'ES', dial: '+34', flag: '🇪🇸', name: 'Spain' },
    { code: 'SE', dial: '+46', flag: '🇸🇪', name: 'Sweden' },
    { code: 'CH', dial: '+41', flag: '🇨🇭', name: 'Switzerland' },
    { code: 'TH', dial: '+66', flag: '🇹🇭', name: 'Thailand' },
    { code: 'TR', dial: '+90', flag: '🇹🇷', name: 'Turkey' },
    { code: 'UA', dial: '+380', flag: '🇺🇦', name: 'Ukraine' },
    { code: 'AE', dial: '+971', flag: '🇦🇪', name: 'United Arab Emirates' },
    { code: 'GB', dial: '+44', flag: '🇬🇧', name: 'United Kingdom' },
    { code: 'US', dial: '+1', flag: '🇺🇸', name: 'United States' },
    { code: 'UY', dial: '+598', flag: '🇺🇾', name: 'Uruguay' },
    { code: 'VE', dial: '+58', flag: '🇻🇪', name: 'Venezuela' },
    { code: 'VN', dial: '+84', flag: '🇻🇳', name: 'Vietnam' },
    { code: 'ZW', dial: '+263', flag: '🇿🇼', name: 'Zimbabwe' },
  ];

  open = false;
  search = '';
  filteredCountries: PhoneCountry[] = [...this.countries];
  selectedCountry: PhoneCountry = this.countries.find((c) => c.code === 'US')!;
  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    public textService: TextService,
    private formData: FormDataService
  ) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    this.filteredCountries = [...this.countries];
    this.selectedCountry = this.countries.find((c) => c.code === 'US')!;
    this.persistPhoneMeta(this.phoneControl.value || '', this.selectedCountry);

    this.phoneControl.valueChanges.subscribe((value) => {
      const raw = value ?? '';
      const clamped = this.clampPhoneValue(raw);
      if (clamped !== raw) {
        this.phoneControl.setValue(clamped, { emitEvent: false });
        this.emitPhoneChange(clamped);
        return;
      }
      this.emitPhoneChange(raw);
    });

    const phoneMeta = this.formData.getPhoneMeta();
    if (phoneMeta?.phone) {
      const savedPhone = phoneMeta.phone;
      let match: PhoneCountry | undefined;
      if (phoneMeta.phoneCountryCode) {
        match = this.countries.find(
          (c) => savedPhone.startsWith(c.dial) && c.code === phoneMeta.phoneCountryCode
        );
      }
      if (!match) {
        match = this.findCountryByDial(savedPhone, phoneMeta.phoneCountryCode);
      }
      if (match) {
        this.selectedCountry = match;
        this.phoneControl.setValue(savedPhone, { emitEvent: false });
        this.persistPhoneMeta(savedPhone, match);
      }
    }
  }

  get showErrors(): boolean {
    return !!(this.ngControl?.touched && this.ngControl?.invalid);
  }

  get errors() {
    return this.ngControl?.errors;
  }

  toggleDropdown() {
    this.open = !this.open;
    if (this.open) {
      this.filteredCountries = [...this.countries];
      this.search = '';
      setTimeout(() => {
        try {
          this.searchInput?.nativeElement.focus();
        } catch {
          // ignore focus errors
        }
      }, 0);
    }
  }

  onSearch(event: Event) {
    const value = (event.target as HTMLInputElement).value.toLowerCase();
    this.search = value;
    this.filteredCountries = this.countries.filter(
      (c) =>
        c.name.toLowerCase().includes(value) ||
        c.dial.includes(value) ||
        c.code.toLowerCase().includes(value)
    );

    setTimeout(() => {
      const el = document.getElementById('country-' + this.filteredCountries[0]?.code);
      el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
  }

  selectCountry(c: PhoneCountry) {
    const previousCountry = this.selectedCountry;
    this.selectedCountry = c;
    this.open = false;

    const currentNational = extractNationalDigits(
      (this.phoneControl.value || '').toString(),
      previousCountry.dial
    );
    const rule = getPhoneCountryRule(c.code);
    const national = rule
      ? clampNationalDigits(currentNational, rule.nationalLength)
      : currentNational.replace(/\D/g, '');

    const formatted = formatPhoneWithDialCode(c.dial, national);
    this.phoneControl.setValue(formatted);
    this.persistPhoneMeta(formatted, c);
    this.onTouched();
    this.ngControl?.control?.updateValueAndValidity({ emitEvent: false });
  }

  onInputBlur() {
    this.onTouched();
  }

  writeValue(value: string | null): void {
    const normalized = value ?? '';
    this.phoneControl.setValue(normalized, { emitEvent: false });

    const meta = this.formData.getPhoneMeta();
    if (meta?.phoneCountryCode) {
      const savedCountry = this.countries.find((c) => c.code === meta.phoneCountryCode);
      if (savedCountry) {
        this.selectedCountry = savedCountry;
      }
    } else if (normalized.startsWith('+')) {
      const inferred = this.findCountryByDial(normalized);
      if (inferred) {
        this.selectedCountry = inferred;
      }
    }

    this.persistPhoneMeta(normalized);
  }

  private clampPhoneValue(value: string): string {
    const rule = getPhoneCountryRule(this.selectedCountry.code);
    if (!rule || !phoneStartsWithDialCode(value, this.selectedCountry.dial)) {
      return value;
    }

    const national = extractNationalDigits(value, this.selectedCountry.dial);
    const clamped = clampNationalDigits(national, rule.nationalLength);
    if (clamped === national) {
      return value;
    }

    return formatPhoneWithDialCode(this.selectedCountry.dial, clamped);
  }

  private emitPhoneChange(value: string): void {
    this.persistPhoneMeta(value);
    this.onChange(value);
    this.ngControl?.control?.updateValueAndValidity({ emitEvent: false });
  }

  private persistPhoneMeta(
    phone: string,
    country: PhoneCountry = this.selectedCountry
  ): void {
    try {
      this.formData.setPhoneMeta({
        phone: phone || undefined,
        phoneCountryCode: country.code,
        phoneDialCode: country.dial,
      });
    } catch {
      // ignore persistence errors
    }
  }

  private findCountryByDial(phone: string, preferredCode?: string): PhoneCountry | undefined {
    if (!phone.startsWith('+')) {
      return undefined;
    }

    const candidates = this.countries
      .filter((country) => phone.startsWith(country.dial))
      .sort((left, right) => right.dial.length - left.dial.length);

    if (!candidates.length) {
      return undefined;
    }

    if (candidates.length === 1) {
      return candidates[0];
    }

    if (preferredCode) {
      const preferred = candidates.find(
        (country) => country.code === preferredCode.toUpperCase()
      );
      if (preferred) {
        return preferred;
      }
    }

    if (candidates.every((country) => country.dial === '+1')) {
      return candidates.find((country) => country.code === 'US') ?? candidates[0];
    }

    return candidates[0];
  }

  registerOnChange(fn: (v: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.phoneControl.disable({ emitEvent: false });
    } else {
      this.phoneControl.enable({ emitEvent: false });
    }
  }
}
